import {
  endOfMonth,
  endOfYear,
  format,
  startOfMonth,
  startOfYear,
  subDays,
} from 'date-fns';
import { supabase } from './supabaseClient';

export type BookingExcludePreset =
  | 'this_year'
  | 'this_month'
  | 'last_30_days'
  | 'last_90_days'
  | 'last_12_months'
  | 'custom';

export interface BookingExcludePeriod {
  start: string;
  end: string;
}

export const BOOKING_EXCLUDE_PRESET_OPTIONS: { id: BookingExcludePreset; label: string }[] = [
  { id: 'this_year', label: 'This year' },
  { id: 'this_month', label: 'This month' },
  { id: 'last_30_days', label: 'Last 30 days' },
  { id: 'last_90_days', label: 'Last 90 days' },
  { id: 'last_12_months', label: 'Last 12 months' },
  { id: 'custom', label: 'Custom date range' },
];

export function resolveBookingExcludePeriod(
  preset: BookingExcludePreset,
  customStart?: string,
  customEnd?: string
): BookingExcludePeriod {
  const today = new Date();

  switch (preset) {
    case 'this_year':
      return {
        start: format(startOfYear(today), 'yyyy-MM-dd'),
        end: format(endOfYear(today), 'yyyy-MM-dd'),
      };
    case 'this_month':
      return {
        start: format(startOfMonth(today), 'yyyy-MM-dd'),
        end: format(endOfMonth(today), 'yyyy-MM-dd'),
      };
    case 'last_30_days':
      return {
        start: format(subDays(today, 30), 'yyyy-MM-dd'),
        end: format(today, 'yyyy-MM-dd'),
      };
    case 'last_90_days':
      return {
        start: format(subDays(today, 90), 'yyyy-MM-dd'),
        end: format(today, 'yyyy-MM-dd'),
      };
    case 'last_12_months':
      return {
        start: format(subDays(today, 365), 'yyyy-MM-dd'),
        end: format(today, 'yyyy-MM-dd'),
      };
    case 'custom':
      return {
        start: customStart?.trim() || '',
        end: customEnd?.trim() || '',
      };
    default:
      return {
        start: format(startOfYear(today), 'yyyy-MM-dd'),
        end: format(endOfYear(today), 'yyyy-MM-dd'),
      };
  }
}

export function formatBookingExcludePeriodLabel(period: BookingExcludePeriod): string {
  if (!period.start || !period.end) return 'selected period';
  if (period.start === period.end) {
    return `on ${format(new Date(`${period.start}T12:00:00`), 'MMM d, yyyy')}`;
  }
  return `from ${format(new Date(`${period.start}T12:00:00`), 'MMM d, yyyy')} to ${format(
    new Date(`${period.end}T12:00:00`),
    'MMM d, yyyy'
  )}`;
}

/** Customer IDs with at least one non-cancelled booking overlapping any period. */
export async function fetchCustomerIdsWithBookingsInPeriods(
  businessId: string,
  periods: BookingExcludePeriod[]
): Promise<Set<string>> {
  const customerIds = new Set<string>();
  if (!periods.length) return customerIds;

  const BATCH_SIZE = 1000;

  for (const period of periods) {
    if (!period.start || !period.end) continue;

    let offset = 0;
    let hasMore = true;

    while (hasMore) {
      const { data, error } = await supabase
        .from('bookings')
        .select('customer_id')
        .eq('business_id', businessId)
        .eq('is_deleted', false)
        .neq('status', 'cancelled')
        .not('customer_id', 'is', null)
        .lte('start_date', period.end)
        .gte('end_date', period.start)
        .range(offset, offset + BATCH_SIZE - 1);

      if (error) {
        console.error('Error fetching bookings for audience exclude:', error);
        throw error;
      }

      if (data?.length) {
        for (const row of data) {
          if (row.customer_id) customerIds.add(row.customer_id);
        }
        hasMore = data.length === BATCH_SIZE;
        offset += BATCH_SIZE;
      } else {
        hasMore = false;
      }
    }
  }

  return customerIds;
}
